import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginStart, loginSuccess, loginFailure } from '../store/slices/authSlice';
import CustomButton from './Button';
import CustomInput from './Input';
import { useAuth } from './hooks/useAuth';
import { validateLogin } from './utils/validation';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const Login = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading = false, error = null } = useSelector((state) => (state && state.auth) || {});
  const { loginUser } = useAuth();

  // Set page title
  React.useEffect(() => {
    document.title = 'ATraqII User Portal';
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = await validateLogin(credentials);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    
    setIsLoading(true);
    dispatch(loginStart());
    try {
      const result = await loginUser(credentials.username, credentials.password);
      if (result && result.token) {
        dispatch(loginSuccess({ username: credentials.username, ...result }));
        navigate('/dashboard');
      } else {
        dispatch(loginFailure(result?.error || 'Invalid credentials'));
      }
    } catch (err) {
      dispatch(loginFailure('Invalid credentials'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="relative min-h-screen flex flex-col justify-between bg-white overflow-hidden">
      {/* Background Video */}
      <video
        className="fixed top-0 left-0 w-full h-full object-cover z-0"
        autoPlay
        loop
        muted
        playsInline
        id="video"
      >
        <source src="/login.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      {/* Optional: Overlay for readability */}
      <div className="fixed top-0 left-0 w-full h-full bg-black/60 z-10 pointer-events-none"></div>

      {/* Main Content (z-20 to be above video/overlay) */}
      <div className="relative z-20 flex flex-1 min-h-screen">
        {/* Left Side */}
        <div className="hidden md:flex flex-col justify-center items-center w-1/2 bg-transparent text-white p-12">
          <h1 className="text-4xl font-bold mb-6">ATraqII User Portal</h1>
          <div className="text-lg space-y-4 text-center w-full max-w-2xl">
            <p>
              Welcome to the ATraqII User Portal, your central hub for control and clarity.
            </p>
            <p>
              Stay informed with the latest updates and news, manage your seat orders with ease, and keep your authorised user list up to date. Need assistance? Our integrated ticketing system ensures you're always supported. Everything you need to manage your ATraqII experience is right here – streamlined, secure, and always at your fingertips.
            </p>
            <p className="font-semibold">
              Control. Clarity. Confidence.
            </p>
          </div>
        </div>
        {/* Right Side (Login Form) */}
        <div className="flex flex-col justify-center items-center w-full md:w-1/2 bg-white p-8 rounded-lg">
          <form onSubmit={handleSubmit} className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-semibold mb-6 text-[#0a1744]">Sign In</h2>
            <p className="mb-6 text-gray-600">Enter your email and password to login</p>
            {error && <div className="text-red-600 mb-4">{error}</div>}
            <CustomInput
              label="Email or User Name"
              name="username"
              type="text"
              placeholder="example@gmail.com"
              value={credentials.username}
              onChange={handleChange}
              required
              className="mb-4"
              error={!!formErrors.username}
              helperText={formErrors.username}
            />
            <CustomInput
              label="Password"
              name="password"
              type="password"
              placeholder="password"
              value={credentials.password}
              onChange={handleChange}
              required
              className="mb-4"
              error={!!formErrors.password}
              helperText={formErrors.password}
            />
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-blue-600 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
            {/* Login Button with loading state */}
            <CustomButton
              type="submit"
              disabled={loading || isLoading}
              className="w-full mt-2 relative"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'SIGN IN'
              )}
            </CustomButton>
            <div className="mt-4 text-xs text-center text-blue-600 cursor-pointer">
              <a 
                href="/terms_condition.pdf" 
                download
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:underline"
              >
                Terms, Conditions and Refund Policy
              </a>
            </div>
          </form>
        </div>
      </div>
      {/* Footer (fixed, overlays both panes) */}
      <footer className="fixed bottom-0 left-0 w-full z-50 flex justify-between items-center px-6 py-2 bg-white border-t text-xs text-gray-500">
        <span>© 2002 - 2025. Comon Professionals Pty. Ltd. All rights reserved.</span>
        <span>Version 1.0</span>
        <span className="flex items-center">
          <span className="fi fi-gb fis mr-1"></span>
          <span className="border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center">🇬🇧</span>
        </span>
      </footer>
    </div>
  );
};

export default Login; 